import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { enrolmentService } from '../../services/enrolmentService';
import { DroneCredentials } from '../../types/enrolment';
import { ApiError } from '../../types/api';
import { renewCommands } from '../../utils/credentials';

/** Operator-facing message for the failure modes this flow can hit. */
const describeError = (err: unknown, fallback: string): string => {
  const status = (err as ApiError)?.status;
  switch (status) {
    case 401:
      return 'Your session has expired — sign in again.';
    case 403:
      return 'You do not have permission to manage drone credentials.';
    case 409:
      return 'A drone with that ID is already enrolled. Pick another ID, or renew that drone with fleetctl renew.';
    default:
      return `${fallback}: ${(err as ApiError)?.message ?? String(err)}`;
  }
};

interface EnrolDroneDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful enrolment so the fleet list can refresh. */
  onEnrolled?: () => void;
}

/** Renders a credential with a copy button and no way to select it by accident. */
const CredentialField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the value is still selectable in the
      // field, so failing silently here leaves the user a way through.
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          value={value}
          size="small"
          fullWidth
          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
          inputProps={{ 'aria-label': label }}
        />
        <Tooltip title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
          <IconButton onClick={copy} aria-label={`Copy ${label.toLowerCase()}`}>
            {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

/**
 * Add a drone without a terminal (issue #470). Renewing one's credentials is
 * `fleetctl renew`, which installs them on the aircraft, so a browser never
 * shows a renewed drone's tokens (#777, C3 (a)).
 *
 * Replaces an eleven-step procedure that required cluster access, the factory
 * key in a shell, and `kubeseal`. The operator's own sign-in is the authority
 * here; the factory key never leaves the backend.
 */
export const EnrolDroneDialog: React.FC<EnrolDroneDialogProps> = ({
  open,
  onClose,
  onEnrolled,
}) => {
  const [droneId, setDroneId] = useState('');
  const [hardwareId, setHardwareId] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<DroneCredentials | null>(null);

  const reset = () => {
    setDroneId('');
    setHardwareId('');
    setDeviceModel('');
    setError(null);
    setCredentials(null);
    setBusy(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await enrolmentService.enrol({
        drone_id: droneId.trim() || undefined,
        // Omitted rather than sent empty: the backend distinguishes "not
        // known yet" from a value, and an empty string is a value (#389).
        hardware_id: hardwareId.trim() || undefined,
        device_model: deviceModel.trim() || undefined,
      });
      setCredentials(result);
      onEnrolled?.();
    } catch (err: unknown) {
      setError(describeError(err, 'Could not reach the fleet service'));
    } finally {
      setBusy(false);
    }
  };

  // Nothing to fill in. Every field on this form is optional now, which is the
  // point: adding an aircraft should not require inventing an identifier for it.
  const canSubmit = true;

  return (
    <Dialog open={open} onClose={credentials ? undefined : close} maxWidth="sm" fullWidth>
      {/* While credentials are on screen the dialog cannot be dismissed by
          clicking away: they are shown once, and losing them to a stray click
          would mean issuing new ones. */}
      <DialogTitle>
        {credentials
          ? 'Credentials — copy them now'
          : 'Add a drone'}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {credentials ? (
          <Stack spacing={2}>
            <Alert severity="warning">
              These are shown once. Install them on the aircraft before closing —
              they cannot be retrieved later, only replaced.
            </Alert>
            {!hardwareId.trim() && (
              <Alert severity="info">
                This aircraft has no hardware identity yet. It reads one from its
                own board and reports it the first time it reaches the fleet.
              </Alert>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">
                Drone ID
              </Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>
                {credentials.drone.id}
              </Typography>
            </Box>
            <CredentialField label="Access token" value={credentials.access_token} />
            <CredentialField label="Refresh token" value={credentials.refresh_token} />
            <Alert severity="info">
              The refresh token expires on{' '}
              <strong>
                {new Date(credentials.refresh_expires_at).toLocaleDateString()}
              </strong>
              . After that the drone cannot reconnect until you renew it with{' '}
              <code>{renewCommands(credentials.drone)[0]}</code>.
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2} mt={1}>
            {/* Optional since #389, and better left blank. The aircraft reads
                its own identity from the board it runs on and reports it on first
                boot — which is more accurate than typing one, and the only way to
                find out that a board has been moved between airframes. A typed
                hardware ID is unique for good once enrolled (#673), so a typo
                takes that identity away from the airframe that needs it. */}
            <TextField
              label="Hardware ID (optional)"
              value={hardwareId}
              onChange={(event) => setHardwareId(event.target.value)}
              helperText="Leave blank: the aircraft reports its own on first boot. A typed one is permanent, so a typo cannot be corrected."
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Hardware ID' }}
            />
            <TextField
              label="Drone ID (optional)"
              value={droneId}
              onChange={(event) => setDroneId(event.target.value)}
              helperText="Leave blank and one will be generated."
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Drone ID' }}
            />
            <TextField
              label="Device model (optional)"
              value={deviceModel}
              onChange={(event) => setDeviceModel(event.target.value)}
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Device model' }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {credentials ? (
          <Button variant="contained" onClick={close}>
            I have saved them
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={busy || !canSubmit}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              Add drone
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
